<template>
  <article
    v-if="flatPageContent && !('location' in flatPageContent)"
    class="promo"
    :class="{ desktop: $isDesktop }"
  >
    <!-- Server-only component renders breadcrumbs, date, title and content without serializing to payload -->
    <FlatPageContent :path="$route.path" :lang="lang" />
  </article>
  <div v-else-if="flatPageContent && 'location' in flatPageContent">
    <p>Redirecting...</p>
  </div>
  <div v-else>
    <p>Loading...</p>
  </div>
</template>

<script setup lang="ts">
import {
  createError,
  navigateTo,
  useAsyncData,
  useHead,
  useNuxtApp,
  useRequestEvent,
  useRoute,
} from 'nuxt/app'
import { computed, onBeforeUnmount, watchEffect } from 'vue'
import SCHEMA_ORGANIZATION from '../../json/schemaOrganization.json'
import { logger } from '../../utility/logger'
import { FlatPagesRepositoryFetch } from './FlatPagesRepositoryFetch'
import type { FlatPage } from './schema'
const route = useRoute()
const { $config } = useNuxtApp()
const { enableCustomHreflangs, disableCustomHreflangs } = useCustomHreflangs()
const lang = computed(() => (route.params.lang as string) || 'en-gb')

definePageMeta({
  validate: (route) => {
    logger('FlatPageComponent.validate', route.path)
    return (
      !route.path.includes('.') &&
      !route.path.startsWith('_') &&
      !route.path.startsWith('/_')
      // Removed trailing slash requirement - let middleware handle it
    )
  },
})

// Fetch flatpage metadata (without large HTML content field)
// Content will be rendered via server-only component to avoid payload duplication
const { data: flatPageContent } = await useAsyncData<
  FlatPage | { location: string }
>(
  `flat-page-${route.path}`,
  async () => {
    // for client if ssr generated error, skip fetching and show error
    if (process.client) {
      const isErrorPage = document.querySelector('.error-wrapper') !== null
      if (isErrorPage) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type this properly as empty response
        return '' as any
      }
    }
    const content = await new FlatPagesRepositoryFetch().get(
      route.path,
      lang.value,
    )
    return content
  },
  {
    transform: (data) => {
      // If it's a redirect, keep the location field
      if (data && 'location' in data) {
        return data
      }
      // Otherwise, strip the large content field for payload optimization
      // Content will be rendered by server-only component
      if (data && 'content' in data) {
        const { content: _content, ...rest } = data
        return rest
      }
      return data
    },
    server: true,
    lazy: false,
  },
)

// Set the xkey header on the server side
if (process.server) {
  const event = useRequestEvent()
  event.node.res.setHeader('xkey', `view:flatpage flatpage:${route.path}`)
  event.node.res.setHeader(
    'Cache-Control',
    `max-age=${$config.nonPriceCacheTime}, public`,
  )
  event.node.res.setHeader('Grace', `${$config.nonPriceGraceTime}`)
}

// Handle redirect or content
if (flatPageContent.value) {
  if ('location' in flatPageContent.value) {
    await navigateTo(flatPageContent.value.location, {
      redirectCode: 301,
      external: true,
    })
  }
} else {
  throw createError({
    statusCode: 404,
    statusMessage: `Page Not Found: ${route.path}`,
    fatal: process.client, // Only set fatal true on client-side
  })
}

// Compute the home path with i18n support
const homePath = computed(() => {
  const prefix = route.params.lang ? '/' + route.params.lang : ''
  return prefix + '/'
})

// Filter out any server breadcrumbs that match the home URL
// Server can send breadcrumbs with or without language prefix, so we filter out both '/' and '/{lang}/'
const filteredBreadcrumbs = computed(() => {
  if (!flatPageContent.value || !('breadcrumbs' in flatPageContent.value)) {
    return []
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type is lost in transform, needs proper typing
  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call -- TODO: Type is lost in transform, needs proper typing
    flatPageContent.value.breadcrumbs?.filter(
      (breadcrumb) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type is lost in transform, needs proper typing
        breadcrumb.url !== '/' && breadcrumb.url !== homePath.value,
    ) || []
  )
})

// eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type is lost in transform, needs proper typing
const title = computed(() => flatPageContent.value?.title || '')
// eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type is lost in transform, needs proper typing
const description = computed(() => flatPageContent.value?.description || '')
// eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type is lost in transform, needs proper typing
const firstPhoto = computed(() => flatPageContent.value?.firstPhoto || null)
const firstPhotoDescription = computed(
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type is lost in transform, needs proper typing
  () => flatPageContent.value?.firstPhotoDescription || '',
)

const articleAbsolutePath = computed(
  () => `https://${$config.public.envUrl}${route.path}`,
)

const ogImageMetaData = computed(() =>
  firstPhoto.value
    ? {
        hid: 'og:image',
        property: 'og:image',
        content: `https://${$config.public.envUrl}${firstPhoto.value}`,
      }
    : {},
)

const ogImageAltMetaData = computed(() =>
  firstPhotoDescription.value
    ? {
        hid: 'og:image:alt',
        property: 'og:image:alt',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
        content: firstPhotoDescription.value,
      }
    : {},
)

const schemaOrganization = {
  type: 'application/ld+json',
  body: true,
  innerHTML: JSON.stringify(SCHEMA_ORGANIZATION),
  hid: 'schema-organization',
}

const newsArticleScript = computed(() => {
  const json: any = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleAbsolutePath.value,
    },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
    headline: title.value,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
    description: description.value,
    author: {
      '@type': 'Organization',
      name: 'Pitchup.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pitchup.com',
      alternateName: 'Pitchup',
      logo: {
        '@type': 'ImageObject',
        url: `https://${$config.public.envUrl}/app-icons/pitchup-icon-1024x1024.png`,
      },
    },
  }
  if (flatPageContent.value?.dateFrom) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
    json.datePublished = flatPageContent.value.dateFrom
  }
  if (firstPhoto.value) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
    json.image = firstPhoto.value
  }
  return {
    type: 'application/ld+json',
    body: true,
    innerHTML: JSON.stringify(json),
  }
})

const breadcrumbList = computed(() => {
  const prefix = route.params.lang ? '/' + route.params.lang : ''
  const json = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': `https://${$config.public.envUrl}${prefix}/`,
          name: 'Home',
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- TODO: Type is lost in transform, needs proper typing
      ...(filteredBreadcrumbs.value?.map((breadcrumb, index) => ({
        '@type': 'ListItem',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
        position: index + 2,
        item: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type is lost in transform, needs proper typing
          '@id': `https://${$config.public.envUrl}${prefix}${breadcrumb.url}`,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type is lost in transform, needs proper typing
          name: breadcrumb.title,
        },
      })) || []),
      {
        '@type': 'ListItem',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type is lost in transform, needs proper typing
        position: (filteredBreadcrumbs.value?.length || 0) + 2,
        item: {
          '@id': articleAbsolutePath.value,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
          name: title.value,
        },
      },
    ],
  }
  return {
    type: 'application/ld+json',
    body: true,
    innerHTML: JSON.stringify(json),
  }
})

const descriptionMetaData = computed(() => ({
  hid: 'description',
  name: 'description',
  property: 'og:description',
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
  content: description.value,
}))

const titleMetaData = computed(() => ({
  hid: 'og:title',
  property: 'og:title',
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
  content: title.value,
}))

const ogUrlMetaData = computed(() => ({
  hid: 'og:url',
  property: 'og:url',
  content: `https://${$config.public.envUrl}${route.path}`,
}))

const hreflangLinks = computed(() => {
  if (
    !flatPageContent.value ||
    !('hreflangs' in flatPageContent.value) ||
    !flatPageContent.value.hreflangs
  ) {
    return []
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-call -- TODO: Type is lost in transform, needs proper typing
  return flatPageContent.value.hreflangs.map((hreflang) => ({
    rel: 'alternate',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type is lost in transform, needs proper typing
    hreflang: hreflang.hreflang,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type is lost in transform, needs proper typing
    href: `https://${$config.public.envUrl}${hreflang.url}`,
    tagPriority: 1,
  }))
})

// Enable custom hreflangs when the backend provides them for this page.
// This watchEffect ensures the state is correct even if data changes reactively.
watchEffect(() => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type is lost in transform, needs proper typing
  if (hreflangLinks.value.length > 0) {
    enableCustomHreflangs()
  } else {
    disableCustomHreflangs()
  }
})

// CRITICAL: Reset custom hreflang state when the component unmounts.
// This ensures that subsequent pages correctly fall back to global hreflang
// generation. Without this, the state would leak and affect other pages.
onBeforeUnmount(() => {
  disableCustomHreflangs()
})

const ogLocaleAlternateMeta = computed(() => {
  if (
    !flatPageContent.value ||
    !('languages' in flatPageContent.value) ||
    !flatPageContent.value.languages
  ) {
    return []
  }

  const currentLocale = (route.params.lang as string) || 'en-gb'

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- TODO: Type is lost in transform, needs proper typing
  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-call -- TODO: Type is lost in transform, needs proper typing
    flatPageContent.value.languages
      .filter((lang) => lang !== currentLocale)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type is lost in transform, needs proper typing
      .map((lang) => ({
        property: 'og:locale:alternate',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type is lost in transform, needs proper typing
        content: lang.replace(/-/g, '_'),
        tagPriority: 1,
      }))
  )
})

useHead(() => ({
  title: `${title.value} | Pitchup.com`,
  meta: [
    titleMetaData.value,
    descriptionMetaData.value,
    ogImageMetaData.value,
    ogImageAltMetaData.value,
    ogUrlMetaData.value,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
    ...ogLocaleAlternateMeta.value,
  ],
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type is lost in transform, needs proper typing
  link: hreflangLinks.value,
  bodyAttrs: {
    class: 'promo-page',
  },
  script: [newsArticleScript.value, breadcrumbList.value, schemaOrganization],
}))
</script>

<style scoped>
.promo {
  padding: 1em 16px 7em;
  width: 100%;
}

.breadcrumb {
  margin: 0 0 1em 0;
}

.breadcrumb li {
  display: inline;
  line-height: 1.2;
  vertical-align: top;
  font-size: var(--small);
}

.breadcrumb li a,
.breadcrumb li a:visited {
  font-weight: normal;
  color: #555;
}

.breadcrumb li:after {
  content: '\203A';
  color: #777;
  margin: 0 5px;
}

.breadcrumb li:last-child a {
  pointer-events: none;
  text-decoration: none;
  color: #999;
}

.breadcrumb li:last-child:after {
  content: '';
  margin: 0;
}

/* desktop & tablet */
article.desktop {
  margin: auto;
  max-width: 50em;
  padding: 0 var(--page-width-padding) 1.4em;
  width: 100%;
}
</style>
